import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Loader2, Check } from 'lucide-react';

/**
 * Inline clickable badge that lets the user "negotiate" with the Coach YUMIX
 * in real-time during a workout. Tapping the badge opens a quick menu with
 * 3 progression modes (Conservateur / Standard / Agressif). Selecting a
 * mode fires onChange(value) so the parent can persist + refresh the
 * recommendations on the fly.
 */
const META = {
  conservative: {
    emoji: '🐢',
    fr: 'Conservateur',
    en: 'Conservative',
    desc_fr: 'Bumps plus petits (+2.5 lbs max). Idéal en retour de blessure ou si tu rates souvent tes cibles.',
    desc_en: 'Smaller bumps (+2.5 lbs max). Best after injury or if you often miss your targets.',
    color: 'sky',
  },
  standard: {
    emoji: '⚡',
    fr: 'Standard',
    en: 'Standard',
    desc_fr: '+5 lbs sur les compounds, +2.5 lbs en isolation. Le défaut équilibré.',
    desc_en: '+5 lbs on compounds, +2.5 lbs on isolation. The balanced default.',
    color: 'amber',
  },
  aggressive: {
    emoji: '🔥',
    fr: 'Agressif',
    en: 'Aggressive',
    desc_fr: '+7.5 lbs sur les compounds, +5 lbs en isolation. Pour les progressions rapides.',
    desc_en: '+7.5 lbs on compounds, +5 lbs on isolation. For fast progression cycles.',
    color: 'rose',
  },
};

const PILL_THEME = {
  sky: 'border-sky-500/40 bg-sky-500/10 text-sky-300 active:bg-sky-500/20',
  amber: 'border-amber-500/40 bg-amber-500/10 text-amber-300 active:bg-amber-500/20',
  rose: 'border-rose-500/40 bg-rose-500/10 text-rose-300 active:bg-rose-500/20',
};

const AggressivenessBadge = ({ value = 'standard', onChange, fr = true, testKey = 'global', compact = false }) => {
  const t = (frTxt, enTxt) => (fr ? frTxt : enTxt);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(null); // value currently being saved
  const safeValue = META[value] ? value : 'standard';
  const meta = META[safeValue];

  const handleOpen = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen(true);
  }, []);

  const handlePick = useCallback(async (newVal) => {
    if (newVal === safeValue || saving) {
      setOpen(false);
      return;
    }
    setSaving(newVal);
    try {
      if (onChange) await onChange(newVal);
    } finally {
      setSaving(null);
      setOpen(false);
    }
  }, [onChange, safeValue, saving]);

  return (
    <>
      {/* Compact pill button — larger touch target for field use */}
      <button
        type="button"
        onClick={handleOpen}
        className={`inline-flex items-center gap-1.5 rounded-full border transition-all active:scale-95 ${
          compact ? 'px-2.5 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'
        } font-bold uppercase tracking-wider ${PILL_THEME[meta.color]}`}
        style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
        data-testid={`aggressiveness-badge-${testKey}`}
        title={t('Ajuster l\'agressivité du Coach YUMIX', 'Adjust Coach YUMIX aggressiveness')}
      >
        <span aria-hidden="true">{meta.emoji}</span>
        <span>{fr ? meta.fr : meta.en}</span>
        <ChevronDown className={compact ? 'w-3 h-3 opacity-70' : 'w-3.5 h-3.5 opacity-70'} />
      </button>

      {/* Quick-pick bottom-sheet — rendered via Portal to document.body to
          escape any ancestor stacking context. Synchronous render (no
          AnimatePresence) so the first tap is never "swallowed". */}
      {open && createPortal(
        <QuickPickSheet
          currentValue={safeValue}
          onPick={handlePick}
          onClose={() => { if (!saving) setOpen(false); }}
          saving={saving}
          fr={fr}
          testKey={testKey}
        />,
        document.body,
      )}
    </>
  );
};

const QuickPickSheet = ({ currentValue, onPick, onClose, saving, fr, testKey }) => {
  const t = (frTxt, enTxt) => (fr ? frTxt : enTxt);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid={`aggressiveness-sheet-${testKey}`}
      style={{
        zIndex: 2147483000,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        pointerEvents: 'auto',
        touchAction: 'none',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card border-t border-x sm:border border-amber-500/40 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[88vh] flex flex-col shadow-2xl yumix-sheet-enter"
        style={{ boxShadow: '0 0 40px rgba(251,191,36,0.20)', pointerEvents: 'auto' }}
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-3 pb-3 border-b border-border/40">
          <div className="flex items-center gap-2">
            <span className="text-base" aria-hidden="true">⚙️</span>
            <span className="font-heading text-xs uppercase tracking-widest text-amber-400">
              {t('Mode de progression', 'Progression mode')}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!!saving}
            className="text-muted-foreground hover:text-foreground p-2 -m-2 disabled:opacity-40"
            data-testid={`aggressiveness-sheet-close-${testKey}`}
            aria-label={t('Fermer', 'Close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-2">
          <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
            {t(
              'Coach YUMIX adapte tes cibles selon le mode choisi. Tu peux ajuster à la volée pendant ta séance.',
              'The Coach YUMIX adapts your targets based on the selected mode. You can adjust on the fly during your session.',
            )}
          </p>

          {['conservative', 'standard', 'aggressive'].map((opt) => {
            const isCurrent = currentValue === opt;
            const isSaving = saving === opt;
            const m = META[opt];
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onPick(opt)}
                disabled={!!saving}
                className={`w-full text-left p-3 rounded-xl border transition-all active:scale-[0.99] ${
                  isCurrent
                    ? 'border-amber-500/60 bg-amber-500/10'
                    : 'border-border/50 bg-secondary/30 hover:border-amber-500/40'
                } ${saving && !isSaving ? 'opacity-50' : ''}`}
                data-testid={`aggressiveness-pick-${opt}`}
              >
                <div className="flex items-start gap-2.5">
                  <span className="text-xl shrink-0 mt-0.5" aria-hidden="true">{m.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-heading uppercase tracking-tight text-foreground flex items-center gap-2">
                      {fr ? m.fr : m.en}
                      {isCurrent && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-amber-400 font-bold">
                          <Check className="w-2.5 h-2.5" /> {t('Actuel', 'Current')}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                      {fr ? m.desc_fr : m.desc_en}
                    </p>
                  </div>
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin text-amber-400 shrink-0 mt-0.5" />}
                </div>
              </button>
            );
          })}

          <p className="text-[10px] text-muted-foreground/60 italic text-center pt-2">
            {t(
              'Astuce : passe en Conservateur les jours de fatigue, en Agressif les jours pleins de jus.',
              'Tip: go Conservative on tired days, Aggressive on high-energy days.',
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AggressivenessBadge;
